// Turning a selection of days into rows, and posting them honestly (W40.1 F7).
//
// Split out of `EventForm.tsx` for two reasons, and the second is the one that
// matters: the fan-out's behaviour at the edges (one day, a contiguous run, a
// scattered set, a retry that must not duplicate) is decidable from a pure
// function, so it can be tested without a form, a router and a mocked network.
//
// The whole design follows from one fact about the backend:
//
//   `CalendarEvent` is `start_date` + nullable `end_date`. One row = one
//   continuous closed interval. No JSON column, no exceptions table, no rrule,
//   and `grid.py` unfolds a row day-by-day with no skip set.
//
// So a non-contiguous selection cannot be one event, whatever the client wants.
// ADR-0101 settles the rest: a **contiguous** selection still posts one range
// row (Q15 — turning a 5-day holiday into 5 rows is a worse product and costs
// nothing), and a **scattered** one posts one row per day.

import type { QueryClient } from "@tanstack/react-query";
import { createEvent, updateEvent, type CalendarEvent, type OwnedCategory, type OwnerToken } from "@/lib/api/calendar";
import { queryKeys } from "@/lib/queries/keys";
import { isContiguous } from "@/lib/validation/calendar";

/** One POST = one stored row. A single day carries `end_date: null` (Q22). */
export interface EventRowPayload {
  category: OwnedCategory;
  title: string;
  start_date: string;
  end_date: string | null;
  owner_id: OwnerToken;
  visibility: "shared" | "private";
}

export interface FanOutFields {
  category: OwnedCategory;
  title: string;
  owner_id: OwnerToken;
  visibility: "shared" | "private";
}

/**
 * Expand a selected set into the rows that will be stored.
 *
 * Contiguous → one row spanning the run (or `end_date: null` for a single day).
 * Scattered → one row per day, ascending.
 *
 * De-duplicates first: the mini calendar toggles rather than appends, so a
 * double-tap cannot produce two — but the setter is cheap insurance against a
 * stale draft re-adding a day.
 */
export function toPayloads(fields: FanOutFields, days: readonly string[]): EventRowPayload[] {
  const sorted = [...new Set(days)].sort();
  if (sorted.length === 0) return [];
  if (isContiguous(sorted)) {
    return [
      {
        ...fields,
        start_date: sorted[0]!,
        // A single day stores end_date = null (Q22), not start == end.
        end_date: sorted.length === 1 ? null : sorted[sorted.length - 1]!,
      },
    ];
  }
  return sorted.map((day) => ({ ...fields, start_date: day, end_date: null }));
}

export interface FanOutOutcome {
  /** `start_date` → event id, for every row that now exists. */
  created: Map<string, string>;
  /** The days that failed, in the order they were attempted. */
  failed: string[];
  /** The first error message, for the banner. */
  firstError: string | null;
}

/**
 * Post the rows that are not already in `created`, and settle **all** of them.
 *
 * `Promise.allSettled`, not a sequential loop with an early return: a user who
 * loses signal halfway through a 12-day month should not have to re-submit the
 * days that already worked to find out which those were. Partial success is the
 * expected case here, not the exception, so it gets first-class handling (D4).
 *
 * `created` is passed in and mutated: the retry path calls this again with the
 * same map, and the `pending` filter then skips what is already there. That is
 * what makes "只補建缺的天數" true rather than approximately true.
 */
export async function submitFanOut(params: {
  fields: FanOutFields;
  days: readonly string[];
  created: Map<string, string>;
  /** When set, every row is a PATCH of this event instead of a POST. */
  editingId?: string | null;
  /**
   * When given, a fan-out that landed at least one row refreshes the home page.
   * This module is a plain async function with no hook access, so the caller
   * hands its client in rather than this reaching for one.
   */
  queryClient?: QueryClient;
}): Promise<FanOutOutcome> {
  const { fields, days, created, editingId, queryClient } = params;
  const pending = toPayloads(fields, days).filter((p) => !created.has(p.start_date));

  const settled = await Promise.allSettled(
    pending.map((p) =>
      editingId
        ? updateEvent(editingId, {
            category: p.category,
            title: p.title,
            start_date: p.start_date,
            end_date: p.end_date,
            owner_id: p.owner_id,
            visibility: p.visibility,
          })
        : createEvent(p),
    ),
  );

  const failed: string[] = [];
  let firstError: string | null = null;
  const rowsBefore = created.size;
  settled.forEach((r, i) => {
    const day = pending[i]!.start_date;
    if (r.status === "fulfilled") {
      const value: CalendarEvent = r.value;
      created.set(day, value.id);
      return;
    }
    failed.push(day);
    if (firstError === null) {
      firstError = r.reason instanceof Error ? r.reason.message : "儲存失敗，請稍候重試";
    }
  });

  // A partial fan-out still wrote rows, so the home page is already stale even
  // though the page reports a failure. Invalidate on the rows that landed, not
  // on the ones that threw.
  if (created.size > rowsBefore) {
    void queryClient?.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  }

  return { created, failed, firstError };
}

/**
 * The success copy, which must not imply one row when there were several.
 *
 * **And it must say *what* happened.** An edit that says 「已建立」 is a false
 * statement about the user's own data — the row was **updated**, not created.
 * That is why `isEdit` is a parameter and not a detail: the same fan-out code
 * path serves both, and only the caller knows which one happened.
 *
 * **One copy source.** This string is used by the form's success toast *and* by
 * the month view's undo bar (via `useCalendarUndoBar`) — so the two surfaces
 * cannot disagree about what just happened. They did once: the bar said
 * 「事件已刪除」 after a create (ADR-0215).
 */
export function successMessage(dayCount: number, isEdit = false): string {
  if (isEdit) {
    return dayCount > 1 ? `已更新 ${dayCount} 天各自的安排` : "事件已更新";
  }
  if (dayCount > 1) return `已建立 ${dayCount} 天各自的安排`;
  return "事件已建立";
}
